import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import driverFrontImg from '../../../assets/images/driver-front.png';
import driverBackImg from '../../../assets/images/driver-back.png';
import passportImg from '../../../assets/images/passport.png';

const DocumentImage = memo(({ imgUrl, onOpenModal, imgStyle, fallbackImg }) => {
  const [loading, setLoading] = useState(false);

  return (
    <>
      {!loading && (
        <Box>
          <CircularProgress color="info" size={40} />
        </Box>
      )}
      <img
        style={{
          ...imgStyle,
          display: loading ? 'inline' : 'none',
        }}
        onLoad={() => {
          setLoading(true);
        }}
        onClick={() => {
          onOpenModal(imgUrl, fallbackImg);
        }}
        src={imgUrl}
        alt="document"
        onError={(e) => {
          setLoading(true);
          if (fallbackImg) {
            e.currentTarget.onerror = null;
            e.currentTarget.src = fallbackImg;
          }
        }}
      />
    </>
  );
});

const ManualKYC = ({
  documentType,
  frontUrl,
  backUrl,
  isLoading,
  isKYCVerified,
  isUpdatingKYC,
  onActivateKYC,
  onAdminApproveKYC,
  isAdminUpdatingKYC,
  isAdminApproved,
}) => {
  const [showModal, setShowModal] = useState(false);

  const [selectedImg, setSelectedImg] = useState('');
  const [fallbackImg, setFallbackImg] = useState('');

  const onOpenModal = (img, fallbackUrl) => {
    setSelectedImg(img);
    setFallbackImg(fallbackUrl);
    setShowModal(true);
  };

  const onCloseModel = () => {
    setSelectedImg('');
    setFallbackImg('');
    setShowModal(false);
  };

  return (
    <>
      <Card sx={{ marginTop: '64px' }}>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Proof of Identity - {documentType ?? 'NA'}
          </MDTypography>
        </MDBox>
        {isLoading ? (
          <Box
            sx={{
              textAlign: 'center',
              paddingTop: '40px',
              paddingBottom: '32px',
            }}
          >
            <CircularProgress color="info" />
          </Box>
        ) : (
          <MDBox mt={3} mb={3} px={2}>
            <Grid container textAlign="center">
              {documentType === 'Passport' ? (
                <Grid item xs={12}>
                  <MDBox sx={{ maxWidth: '100%' }}>
                    <DocumentImage
                      imgUrl={frontUrl}
                      fallbackImg={passportImg}
                      onOpenModal={onOpenModal}
                      imgStyle={{
                        cursor: 'pointer',
                        objectFit: 'contain',
                        maxHeight: '400px',
                        maxWidth: '100%',
                      }}
                    />
                  </MDBox>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ maxWidth: '100%' }}>
                      <MDTypography textAlign="center" mb={2}>
                        Front side
                      </MDTypography>
                      <DocumentImage
                        imgUrl={frontUrl}
                        fallbackImg={driverFrontImg}
                        onOpenModal={onOpenModal}
                        imgStyle={{
                          cursor: 'pointer',
                          objectFit: 'contain',
                          maxHeight: '400px',
                          maxWidth: '100%',
                        }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox sx={{ maxWidth: '100%' }}>
                      <MDTypography textAlign="center" mb={2}>
                        Back side
                      </MDTypography>
                      <DocumentImage
                        imgUrl={backUrl}
                        fallbackImg={driverBackImg}
                        onOpenModal={onOpenModal}
                        imgStyle={{
                          cursor: 'pointer',
                          objectFit: 'contain',
                          maxHeight: '400px',
                          maxWidth: '100%',
                        }}
                      />
                    </MDBox>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <MDButton
                  isLoading={isUpdatingKYC}
                  disabled={isKYCVerified || isUpdatingKYC}
                  color="success"
                  type="gradient"
                  onClick={onActivateKYC}
                  style={{marginRight:'8px'}}
                >
                  {isKYCVerified ? 'KYC Approved' : 'KYC Approve'}
                </MDButton>
                <MDButton
                  isLoading={isAdminUpdatingKYC}
                  disabled={isAdminApproved || isAdminUpdatingKYC}
                  color="success"
                  type="gradient"
                  onClick={onAdminApproveKYC}
                >
                  {isAdminApproved ? 'Admin Approved' : 'Admin Approve'}
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </Card>
      {showModal && (
        <Modal open={showModal} onClose={onCloseModel}>
          <MDBox
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              height: '90%',
              maxHeight: 'calc(100vh - 100px)',
              overflow: 'auto',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <MDTypography variant="h4" component="h2">
                Document
              </MDTypography>
              <IconButton onClick={onCloseModel}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </MDBox>
            <DocumentImage
              imgUrl={selectedImg}
              onOpenModal={() => {}}
              fallbackImg={fallbackImg}
              imgStyle={{
                objectFit: 'contain',
                display: 'block',
                width: '100%',
                height: 'calc(100% - 100px)',
              }}
            />
          </MDBox>
        </Modal>
      )}
    </>
  );
};

ManualKYC.propTypes = {
  documentType: PropTypes.string,
  frontUrl: PropTypes.string,
  backUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  isKYCVerified: PropTypes.bool,
  isAdminUpdatingKYC: PropTypes.bool,
  isAdminApproved: PropTypes.bool,
  isUpdatingKYC: PropTypes.bool,
  onActivateKYC: PropTypes.func,
  onAdminApproveKYC: PropTypes.func,
};

ManualKYC.defaultProps = {
  documentType: '',
  frontUrl: '',
  backUrl: '',
  isLoading: false,
  isKYCVerified: false,
  isAdminUpdatingKYC: false,
  isAdminApproved: false,
  isUpdatingKYC: false,
  onActivateKYC: () => {},
  onAdminApproveKYC: () => {},
};

export default ManualKYC;
